import React from 'react'
import { Link, matchPath } from 'react-router-dom'
import { theme } from '../../../theme/theme'
import { Icon, IconChoices } from '../Icon'
import { useIsMobile } from '../../../hooks/useIsMobile'
import { useHamburger } from '../../../hooks/useHamburgerContext'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { NotificationBadge } from '../../notifications/NotificationBadge/NotificationBadge'

export interface MenuitemTitleProps {
  title: string
  icon: IconChoices
  isSelected?: boolean
  displayNotificationBadge?: boolean
  showAlert?: boolean
  destructive?: boolean
  className?: string
  justifyCenter?: boolean
  newFeature?: boolean
}

const MenuItemTitle = ({
  isSelected,
  className,
  icon,
  title,
  showAlert,
  displayNotificationBadge,
  destructive,
  justifyCenter,
  newFeature,
}: MenuitemTitleProps): JSX.Element => {
  const { translate } = useAppTranslation()
  const getIconColor = (): string => {
    if (destructive) {
      return 'red'
    }
    if (isSelected) {
      return 'white'
    }
    return 'black'
  }
  return (
    <div
      className={`flex h-12 items-center rounded ${
        justifyCenter ? 'justify-center' : 'justify-between'
      } px-4
      ${
        isSelected
          ? 'border border-brandGreen bg-brandGreen text-white'
          : 'hover:bg-neutral-96'
      } ${className ?? ''}`}
    >
      <div className="flex items-center gap-2">
        <Icon icon={icon} color={getIconColor()} />
        <span
          style={theme.textVariants.captionBold}
          className={`${destructive ? 'text-red' : ''}`}
        >
          {title}
        </span>
        {newFeature && (
          <span
            style={theme.textVariants.fineprintBold}
            className="text-brandGreen"
          >
            {translate('common.newFeature')}
          </span>
        )}
      </div>
      <div className="flex gap-2">
        {showAlert && <Icon icon={IconChoices.EXCLAMATION_ALERT} />}
        {displayNotificationBadge && (
          <NotificationBadge
            className="ml-2 mt-[2px]"
            show
            absoluteOffset={false}
          />
        )}
      </div>
    </div>
  )
}

export interface MenuItem {
  title: string
  icon: IconChoices
  onSelect?: () => void
  isSelected?: boolean
  url?: string
  urlIsExternal?: boolean
  onClick?: () => void
  showAlert?: boolean
  className?: string
  destructive?: boolean
  trackingCallback?: () => void
  disabled?: boolean
  newFeature?: boolean
  matchPattern?: string
  displayNotificationBadge?: boolean
}

export const MenuItem = ({
  title,
  icon,
  url,
  urlIsExternal,
  onClick,
  showAlert,
  className,
  isSelected,
  destructive,
  trackingCallback,
  onSelect,
  disabled,
  newFeature,
  matchPattern,
  displayNotificationBadge,
}: MenuItem): JSX.Element | null => {
  const isMobile = useIsMobile()
  const { setDisplaySidebar } = useHamburger()
  const matches = matchPattern && url ? matchPath(matchPattern, url) : null

  const _onClick = (): void => {
    if (!disabled) {
      if (trackingCallback) trackingCallback()
      if (onSelect) onSelect()
      if (isMobile) setDisplaySidebar(false)
    }
  }

  if (onClick) {
    return (
      <button className="w-full" onClick={onClick} disabled={disabled}>
        <MenuItemTitle
          title={title}
          icon={icon}
          showAlert={showAlert}
          className={className}
          isSelected={isSelected}
          destructive={destructive}
          newFeature={newFeature}
          displayNotificationBadge={displayNotificationBadge}
          justifyCenter
        />
      </button>
    )
  }

  if (url) {
    return urlIsExternal ? (
      <a
        href={url}
        target="_blank"
        rel="noreferrer noopener"
        className={`${disabled ? 'pointer-events-none opacity-50' : ''}`}
      >
        <MenuItemTitle
          title={title}
          icon={icon}
          showAlert={showAlert}
          className={className}
          isSelected={isSelected}
          destructive={destructive}
          newFeature={newFeature}
          displayNotificationBadge={displayNotificationBadge}
        />
      </a>
    ) : (
      <Link
        to={url}
        onClick={_onClick}
        className={`${disabled ? 'pointer-events-none opacity-50' : ''}`}
      >
        <MenuItemTitle
          title={title}
          icon={icon}
          showAlert={showAlert}
          className={className}
          isSelected={isSelected || !!matches}
          destructive={destructive}
          newFeature={newFeature}
          displayNotificationBadge={displayNotificationBadge}
        />
      </Link>
    )
  }

  return null
}
